"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiController = void 0;
const hal_controller_1 = require("./hal.controller");
const isBrowser = typeof window !== "undefined";
const commandsKey = "app:commands";
class ApiController {
    /*
     * Constructor
     */
    constructor(url, refreshToken) {
        /*
         * Private properties
         */
        this._visitor = new Map();
        this._refreshTokenPromise = null;
        this._cookie = "";
        this._userAgent = isBrowser ? window.navigator.userAgent : "";
        this._subscribers = new Set();
        /*
         * Public properties
         */
        this.debugMode = process.env.DEBUG_MODE === "true";
        this.onMe = null;
        if (url) {
            this.initialize(url, refreshToken);
        }
    }
    /*
     * Private methods
     */
    getHeaders(init = {}) {
        const headers = new Headers(init.headers);
        if (this._cookie) {
            headers.set("Cookie", this._cookie);
        }
        headers.set("X-Audioteka-Device", `AudiotekaWeb/1.0.1 Web/3.0 (Browser;${this._userAgent})`);
        return headers;
    }
    refreshToken() {
        if (!this._refreshTokenPromise && this._refreshToken) {
            this._refreshTokenPromise = this.sendCommand(this._refreshToken.command, this._refreshToken.payload).finally(() => {
                setTimeout(() => (this._refreshTokenPromise = null), 10000); // 10 seconds threshold between requests
            });
        }
        return this._refreshTokenPromise;
    }
    /*
     * Getters
     */
    get urlOrigin() {
        if (!this._urlOrigin) {
            if (!this._url) {
                throw new Error("Initialize API first!");
            }
            this._urlOrigin = new URL(this._url).origin;
        }
        return this._urlOrigin;
    }
    /*
     * Public methods
     */
    initialize(url, refreshToken) {
        const _url = new URL(url);
        if (_url.pathname === "/") {
            _url.pathname = "/v2";
        }
        this._url = _url.toString();
        this._hal = new hal_controller_1.HalController(_url.pathname);
        this._refreshToken = refreshToken;
    }
    setCookie(cookie) {
        this._cookie = cookie;
        return this;
    }
    setUserAgent(ua) {
        this._userAgent = ua;
        return this;
    }
    /**
     * Gets Cookie & UserAgent from headers.
     * Can be used on SSR to pass user's credentials
     * @param req
     */
    authRequest(req) {
        const headers = new Headers(req.headers);
        if (headers.has("Cookie")) {
            this._cookie = headers.get("Cookie");
        }
        if (headers.has("User-Agent")) {
            this._userAgent = headers.get("User-Agent");
        }
        return this;
    }
    request(endpoint, query = {}, init = {}, followedKey, tryRefreshToken = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._url) {
                throw new Error("Initialize API first!");
            }
            const fetchInit = Object.assign(Object.assign({ credentials: "include" }, init), { headers: this.getHeaders(init) });
            if (fetchInit.body && typeof fetchInit.body !== "string") {
                fetchInit.body = JSON.stringify(fetchInit.body);
            }
            const fetchInfo = new URL(this.urlOrigin + endpoint);
            if (query) {
                Object.keys(query).forEach((key) => {
                    const regexKey = new RegExp(`({|%7B)${key}(}|%7D)`); // {key}
                    const value = String(query[key]);
                    if (regexKey.test(fetchInfo.pathname)) {
                        fetchInfo.pathname = fetchInfo.pathname.replace(regexKey, value);
                    }
                    else {
                        fetchInfo.searchParams.set(key, value);
                    }
                });
            }
            let response;
            const isCommand = fetchInit.method === "POST" && followedKey === commandsKey;
            const commandName = isCommand
                ? JSON.parse(fetchInit.body).name
                : void 0;
            try {
                response = yield fetch(fetchInfo, fetchInit);
            }
            finally {
                this._subscribers.forEach((subscriber) => {
                    subscriber({
                        commandName,
                        followedKey,
                        isCommand,
                        method: fetchInit.method,
                        payload: fetchInit.body ? JSON.parse(fetchInit.body) : {},
                        response: response ? response.clone() : null,
                        url: fetchInfo.toString(),
                    });
                });
            }
            if (this.debugMode) {
                console.log(fetchInit.method, response === null || response === void 0 ? void 0 : response.status, fetchInfo.toString());
            }
            // Sometimes response is empty so don't throw error on invalid JSON
            const responseBody = response ? yield response.clone().json().catch(() => ({})) : {};
            if (response === null || response === void 0 ? void 0 : response.ok) {
                return responseBody;
            }
            if ((response === null || response === void 0 ? void 0 : response.status) === 401 &&
                tryRefreshToken &&
                this._refreshToken &&
                commandName !== this._refreshToken.command &&
                commandName !== "AuthenticateForWeb") {
                const tokenResponse = yield this.refreshToken();
                if (tokenResponse.expires_at) {
                    return this.request(endpoint, query, init, followedKey, false);
                }
            }
            const error = new Error(`Error ${response === null || response === void 0 ? void 0 : response.status} on ${fetchInfo.toString()}: ${response === null || response === void 0 ? void 0 : response.statusText}`);
            error.response = response ? response.clone() : null;
            throw error;
        });
    }
    get(endpoint, query = {}, init = {}, followedKey) {
        return this.request(endpoint, query, Object.assign(Object.assign({}, init), { method: "GET" }), followedKey);
    }
    post(endpoint, body, query = {}, init = {}, followedKey) {
        return this.request(endpoint, query, Object.assign(Object.assign({}, init), { body, method: "POST" }), followedKey);
    }
    follow(name, query = {}, init = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._hal) {
                throw new Error("Initialize API first!");
            }
            const ctrl = yield this._hal.follow(name, query, init);
            if (name === "app:me" && this.onMe) {
                this.onMe(ctrl);
            }
            return ctrl;
        });
    }
    sendCommand(name, data = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._hal) {
                throw new Error("Initialize API first!");
            }
            yield this._hal.get();
            const { href } = this._hal.links[commandsKey];
            return this.post(href, Object.assign({ name }, data), {}, {}, commandsKey);
        });
    }
    getVisitor(lang = "") {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._hal) {
                throw new Error("Initialize API first!");
            }
            if (!this._visitor.has(lang)) {
                const fetchVisitor = () => __awaiter(this, void 0, void 0, function* () {
                    if (lang) {
                        yield this._hal.get();
                        const { href = "" } = this._hal.links["app:visitor"];
                        const resource = yield this.get(href.replace(/($|\/$)/, `/${lang}`));
                        return hal_controller_1.HalController.fromObject(resource);
                    }
                    return this.follow("app:visitor");
                });
                this._visitor.set(lang, fetchVisitor());
            }
            return (yield this._visitor.get(lang));
        });
    }
    subscribe(subscriber) {
        this._subscribers.add(subscriber);
        return () => this.unsubscribe(subscriber);
    }
    unsubscribe(listener) {
        this._subscribers.delete(listener);
    }
}
exports.ApiController = ApiController;
